import React, { Fragment, useEffect } from "react";
import { ToastContainer } from "react-toastify";
import { useContext } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { CSSTransition, TransitionGroup } from "react-transition-group";

import Taptop from "./TapTop";
import Header from "./Header";
import Sidebar from "./Sidebar";
import ThemeCustomize from "../Layout/ThemeCustomizer";
import Footer from "./Footer";
import CustomizerContext from "../_helper/Customizer";
import AnimationThemeContext from "../_helper/AnimationTheme";
import ConfigDB from "../Config/ThemeConfig";
import Loader from "./Loader";
import ChatbotLauncher from "../Components/V3/Chatbot/ChatbotLauncher";

// Routes that render in fullbleed (no sidebar). Driven by URL, not by the page
// component's mount lifecycle, because CSSTransition keeps two pages mounted
// briefly during navigation — page-level useEffect runs too late.
const FULLBLEED_ROUTES = ['/v3/spatial', '/admin/home', '/admin/viewapikey', '/admin/deletedata'];

// Any route under /v3/* gets the v3-themed body class so v3-theme.css can
// repaint body / page-body-wrapper backgrounds (dark or light) without
// affecting V2 pages, which were authored against the legacy light shell.
const V3_ROUTE_PREFIX = '/v3';

// Legacy /admin routes that have been redesigned to the v3 vibe. Listed
// explicitly because only some /admin pages have been upgraded — broad
// /admin/* would repaint the rest of the V2 surface and break it.
const V3_ADMIN_ROUTES = [
  '/admin/home',
  '/admin/viewapikey',
  '/admin/deletedata',
  '/admin/settings',
  '/admin/country',
  '/admin/property',
  '/admin/category',
  '/admin/subcategory',
  '/admin/tenant',
  '/admin/tenantportal',
  '/admin/usermanagement',
  '/admin/healthcheckup',
  '/admin/schedulereports',
  '/admin/logs',
  '/admin/licenses',
  '/controlpanel',
];

// Premium skin (opt-in via the sidebar ✦ toggle) applies to every V3 + migrated
// admin route EXCEPT Spatial Brief, which is intentionally left in the original
// look. Driven by `body.v3-premium`; the preference lives in localStorage and is
// written by useSkin — Layout owns the route gating so spatial stays untouched.
const PREMIUM_EXCLUDED_PREFIX = '/v3/spatial';

const AppLayout = ({ children, classNames, ...rest }) => {
  const { layout } = useContext(CustomizerContext);
  const { sidebarIconType } = useContext(CustomizerContext);

  const layout1 = localStorage.getItem("sidebar_layout") || layout;
  const sideBarIcon = localStorage.getItem("sidebar_icon_type") || sidebarIconType;
  const location = useLocation();
  // Sub-path deploys (homepage=/v3.0, basename="/") carry the PUBLIC_URL prefix
  // on location.pathname, so the bare route constants below wouldn't match
  // (e.g. "/v3.0/v3/spatial" vs "/v3/spatial") and the sidebar would show on
  // Spatial Brief. Strip the prefix once so matching behaves the same as at
  // root. No-op when PUBLIC_URL is empty (the public root build).
  const PUBLIC_URL = process.env.PUBLIC_URL || '';
  const routePath = PUBLIC_URL && location.pathname.startsWith(PUBLIC_URL)
    ? (location.pathname.slice(PUBLIC_URL.length) || '/')
    : location.pathname;
  const { animation } = useContext(AnimationThemeContext);
  const animationTheme = localStorage.getItem("animation") || animation || ConfigDB.data.router_animation;

  // Toggle body.v3-fullbleed based on the current route. Re-evaluates on every
  // navigation, so going back to a fullbleed route re-applies the class even
  // though the page component itself may not have unmounted yet.
  useEffect(() => {
    const isFullbleed = FULLBLEED_ROUTES.some(p => routePath === p || routePath.startsWith(p + '/'));
    if (isFullbleed) document.body.classList.add('v3-fullbleed');
    else document.body.classList.remove('v3-fullbleed');

    const isV3Prefix = routePath === V3_ROUTE_PREFIX || routePath.startsWith(V3_ROUTE_PREFIX + '/');
    const isV3Admin = V3_ADMIN_ROUTES.some(p => routePath === p || routePath.startsWith(p + '/'));
    if (isV3Prefix || isV3Admin) document.body.classList.add('v3-themed');
    else document.body.classList.remove('v3-themed');

    const isSpatial = routePath === PREMIUM_EXCLUDED_PREFIX
      || routePath.startsWith(PREMIUM_EXCLUDED_PREFIX + '/');
    // Paint the spatial route dark immediately so there's no white flash before
    // the cinematic SpatialLandingPage mounts (its loader + canvas are dark).
    document.body.classList.toggle('v3-spatial-route', isSpatial);

    // Premium skin: on iff the user opted in AND we're not on Spatial Brief.
    // Re-evaluated on every navigation, and live via the `v3-skin-change` event
    // dispatched by useSkin so the sidebar toggle takes effect without a reload.
    const applyPremium = (e) => {
      // Prefer the skin carried on the event (set synchronously by useSkin) over
      // localStorage — the storage write can lag the dispatch by a tick, which
      // made theme switches only "take" on the next navigation.
      const skin = (e && e.detail) || localStorage.getItem('v3_skin') || 'aurora';
      const on = skin !== 'classic' && !isSpatial;
      document.body.classList.toggle('v3-premium', on);
      if (on) document.body.setAttribute('data-pm-theme', skin);
      else document.body.removeAttribute('data-pm-theme');
    };
    applyPremium();
    window.addEventListener('v3-skin-change', applyPremium);

    // Reset scroll on route change. React Router v6 does not auto-restore
    // scroll, so navigating from a scrolled-down page leaves window/body at
    // the previous offset — the new page appears to "load at the bottom".
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    if (document.body) document.body.scrollTop = 0;
    const pageBody = document.querySelector('.page-body');
    if (pageBody) pageBody.scrollTop = 0;

    return () => window.removeEventListener('v3-skin-change', applyPremium);
  }, [location.pathname]);

  return (
    <Fragment>
      <Loader />
      <Taptop />
      <div className={`page-wrapper ${layout1}`} sidebar-layout={sideBarIcon} id="pageWrapper">
        <Header />
        <div className="page-body-wrapper">
          <Sidebar />
          {/* Key the page transition on the PATHNAME, not location.key. React
              Router mints a new location.key on every navigation — including the
              `navigate(..., {replace:true})` writes the dashboard filter provider
              fires to sync filters into the URL. Keying on location.key therefore
              remounts the whole page on each filter/URL change, which re-hydrates
              the provider (URL churn / "fluctuation") and re-runs entrance
              animations. Pathname keys animate real page changes only; query
              updates are handled reactively without a remount. */}
          <TransitionGroup {...rest}>
            <CSSTransition key={location.pathname} timeout={100} classNames={animationTheme} unmountOnExit>
              <div className="page-body">
                <div>
                  <div>
                    <Outlet />
                  </div>
                </div>
              </div>
            </CSSTransition>
          </TransitionGroup>
          <Footer />
        </div>
      </div>
      <ThemeCustomize />
      <ToastContainer />
      {/* Role, route and per-concessionnaire gating all live in
          useChatbotAccess, which the sidebar "Ask anything…" bar shares — so the
          launcher mounts everywhere and hides itself. */}
      <ChatbotLauncher />
    </Fragment>
  );
};
export default AppLayout;
